/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  CHART_HEIGHT,
  ChartEmpty,
  buildMalhaMaterialChartModel,
  buildMalhaMaterialStackedSeries,
  getMaterialChartLegendProps,
} from './chartShared';
import { formatKgMetric } from './constants';

const MALHA_X_AXIS_ID = 'malha-x-axis';

const formatTooltipValue = (value, label) => {
  const kg = formatKgMetric(value);
  return label ? `${label}: ${kg} kg` : `${kg} kg`;
};

const MaterialByMalhaChart = ({
  items,
  materialColumns,
  loading,
  height,
  onSegmentSelect,
}) => {
  const theme = useTheme();

  const { dataset, materialColumns: resolvedColumns } = useMemo(
    () => buildMalhaMaterialChartModel(items, materialColumns),
    [items, materialColumns],
  );

  const series = useMemo(
    () => buildMalhaMaterialStackedSeries(resolvedColumns, theme, formatTooltipValue),
    [resolvedColumns, theme],
  );

  const chartHeight = height || CHART_HEIGHT;
  const hasData = dataset.length > 0;
  const axisLabelColor = theme.palette.text.secondary;

  const handleItemClick = useCallback(
    (_, params) => {
      if (!onSegmentSelect) return;
      const row = dataset[params?.dataIndex];
      const materialCol = resolvedColumns[params?.seriesIndex];
      if (!row?.malhaId) return;
      onSegmentSelect({
        malhaId: row.malhaId,
        malhaCodigo: row.malhaLabel,
        materialTipo: materialCol?.tipo,
        materialLabel: materialCol?.label,
      });
    },
    [dataset, onSegmentSelect, resolvedColumns],
  );

  if (loading) {
    return <Skeleton variant="rectangular" height={chartHeight} sx={{ borderRadius: 1 }} />;
  }

  if (!hasData) {
    return <ChartEmpty message="Sem dados por malha no período" />;
  }

  return (
    <Box sx={{ width: '100%', minHeight: chartHeight }}>
      <BarChart
        height={chartHeight}
        dataset={dataset}
        xAxis={[
          {
            id: MALHA_X_AXIS_ID,
            scaleType: 'band',
            dataKey: 'malhaLabel',
            label: 'Malha',
            labelStyle: {
              fill: axisLabelColor,
              fontSize: 11,
              fontWeight: 500,
            },
            tickLabelStyle: {
              fill: axisLabelColor,
              fontSize: 11,
            },
          },
        ]}
        yAxis={[{ label: 'kg', min: 0 }]}
        series={series}
        margin={{ left: 48, right: 16, top: 8, bottom: 24 }}
        onItemClick={onSegmentSelect ? handleItemClick : undefined}
        slotProps={{
          bar: onSegmentSelect ? { style: { cursor: 'pointer' } } : undefined,
          legend: getMaterialChartLegendProps(),
        }}
      />
    </Box>
  );
};

MaterialByMalhaChart.propTypes = {
  items: PropTypes.array,
  materialColumns: PropTypes.array,
  loading: PropTypes.bool,
  height: PropTypes.number,
  onSegmentSelect: PropTypes.func,
};

MaterialByMalhaChart.defaultProps = {
  items: [],
  materialColumns: [],
  loading: false,
  height: null,
  onSegmentSelect: null,
};

export default MaterialByMalhaChart;
